<template>
  <div
    class="sponsor-picker-modal flex flex-col h-full bg-white rounded-2xl overflow-hidden shadow-2xl"
  >
    <!-- Header -->
    <header
      class="px-4 sm:px-6 py-4 bg-slate-900 text-white flex items-center justify-between border-b border-slate-850 relative"
    >
      <div class="relative z-10 flex items-center gap-2">
        <i class="fal fa-handshake text-teal-400 text-lg"></i>
        <h2 class="text-base font-extrabold tracking-tight">
          {{ $t('Components.SponsorPickerModal.title') }}
        </h2>
      </div>
      <button
        type="button"
        class="close-btn relative z-10 text-slate-400 hover:text-white transition-colors text-xl leading-none rounded-full hover:bg-white/10 flex items-center justify-center focus:outline-none"
        @click="close"
        :aria-label="$t('Components.SponsorPickerModal.close')"
      >
        &times;
      </button>
    </header>

    <SponsorsDataProvider v-slot="{ sponsorsSortedByOrder, loading }">
      <div class="flex flex-col flex-1 min-h-0">
        <!-- Search -->
        <div class="px-4 sm:px-6 pt-4 pb-2 shrink-0">
          <div class="relative">
            <span
              class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400"
            >
              <i class="fal fa-search"></i>
            </span>
            <input
              v-model="search"
              type="text"
              :placeholder="
                $t('Components.SponsorPickerModal.searchPlaceholder')
              "
              class="w-full pl-10 pr-4 py-2.5 bg-slate-50 text-slate-800 text-sm rounded-xl border border-slate-200 focus:border-teal-500 focus:bg-white focus:ring-2 focus:ring-teal-100 outline-none transition-all min-h-[44px]"
            />
          </div>
        </div>

        <!-- List -->
        <div class="flex-1 overflow-y-auto px-4 sm:px-6 py-2 space-y-2">
          <div v-if="loading" class="py-10 text-center text-sm text-slate-500">
            {{ $t('Components.SponsorPickerModal.loading') }}
          </div>
          <div
            v-else-if="visible(sponsorsSortedByOrder).length === 0"
            class="py-10 text-center text-sm text-slate-500"
          >
            {{ $t('Components.SponsorPickerModal.empty') }}
          </div>
          <button
            v-for="sponsor in visible(sponsorsSortedByOrder)"
            :key="sponsor.id"
            type="button"
            class="w-full flex items-center gap-3 p-3 rounded-2xl border border-slate-200 hover:border-teal-500 hover:bg-teal-50/20 transition-all text-left min-h-[60px]"
            @click="pick(sponsor)"
          >
            <div
              class="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0"
            >
              <img
                v-if="sponsor.logoUrl"
                :src="sponsor.logoUrl"
                :alt="sponsor.name"
                class="w-full h-full object-contain"
              />
              <span v-else class="text-sm font-black text-slate-400">
                {{ (sponsor.name || '?')[0].toUpperCase() }}
              </span>
            </div>
            <span
              class="flex-1 min-w-0 text-sm font-bold text-slate-800 truncate"
            >
              {{ sponsor.name }}
            </span>
            <i class="fal fa-arrow-right text-slate-300 text-sm shrink-0"></i>
          </button>
        </div>
      </div>
    </SponsorsDataProvider>
  </div>
</template>

<script>
import BookMeetingModal from './BookMeetingModal.vue';
import { englishOnlyI18nMixin } from '@/mixins';

export default {
  name: 'SponsorPickerModal',
  mixins: [englishOnlyI18nMixin],
  data() {
    return {
      search: ''
    };
  },
  methods: {
    visible(sponsors) {
      const q = this.search.trim().toLowerCase();
      if (!q) return sponsors;
      return sponsors.filter((s) => (s.name || '').toLowerCase().includes(q));
    },
    close() {
      this.$emit('close');
    },
    pick(sponsor) {
      if (!sponsor || !sponsor.id) return;
      // Chain straight into the existing booking modal; duplicate-meeting requests are
      // guarded server-side and surfaced there.
      this.$emit('close');
      this.$modal.show(
        BookMeetingModal,
        {
          sponsorId: sponsor.id,
          sponsorName: sponsor.name || '',
          boothLabel: '',
          english: true
        },
        {
          adaptive: true,
          minWidth: 320,
          maxWidth: 600,
          width: '90%',
          height: 'auto'
        }
      );
    }
  }
};
</script>

<style scoped>
.bg-slate-850 {
  background-color: #0f172a;
}
i.fal,
i.fas,
i.far {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  flex-shrink: 0 !important;
}
.close-btn {
  width: 2.75rem !important;
  height: 2.75rem !important;
  min-width: 2.75rem !important;
  min-height: 2.75rem !important;
}
@media (min-width: 640px) {
  .close-btn {
    width: 2rem !important;
    height: 2rem !important;
    min-width: 2rem !important;
    min-height: 2rem !important;
  }
}
.sponsor-picker-modal {
  max-height: 80vh;
}
</style>
